// DealerTerritoryTab.tsx — Coverage-Matrix Wave 2 (Squad UI-B).
// The "Dealer Territory" hub tab: a completeness matrix of dealer SERVICE
// ASSIGNMENT (product-group × state, drillable to district → pincode), distinct
// from the dealer LOCATION map. Green = every pincode here has a dealer assigned
// to serve it, yellow = some do, red = none. Binds CE's Wave-1 endpoints
// (dealer-territory-matrix + coverage-drill?layer=DEALER + territory-detail).
//
// Assigning a dealer to a territory: dealers are proximity-ranked (nearby-first,
// spec §5) and the dealer-requires-exec guard (spec §6) is enforced — a dealer
// can only be assigned where an exec already owns the (product, territory).

import { useEffect, useMemo, useState } from 'react';
import { Store, MapPin, ChevronRight, AlertCircle, RefreshCcw, ArrowLeft, ShieldAlert } from 'lucide-react';
import {
  leadRouterV2Api,
  type CoverageMatrixV2Response,
  type MatrixTone,
  type MatchTypeV2,
  type TerritoryDetailV2,
} from '@/api/leadRouter';
import {
  rankDealersByProximity, proximityScore, proximityLabel,
  type DealerLocus, type TerritoryTarget,
} from './dealerProximity';
import { dealerAssignBlockReason, dealerAssignErrorMessage, DEALER_REQUIRES_EXEC_MESSAGE } from './dealerGuard';

const TONE_BG: Record<MatrixTone, string> = {
  green: 'var(--forest-100, #d6ecd9)',
  yellow: 'var(--warn-bg, #f7eccf)',
  red: 'repeating-linear-gradient(45deg, #f6d5d5, #f6d5d5 5px, #fbe4e4 5px, #fbe4e4 10px)',
};
const TONE_FG: Record<MatrixTone, string> = {
  green: 'var(--forest, #2f7d3b)',
  yellow: 'var(--warn, #a5741a)',
  red: 'var(--danger, #b5433a)',
};
const TONE_GLYPH: Record<MatrixTone, string> = { green: '●', yellow: '◐', red: '—' };

interface DrillFrame { productGroupKey: string; state: string; district?: string }

export function DealerTerritoryTab({ dealers = [] }: { dealers?: DealerLocus[] }) {
  const [matrix, setMatrix] = useState<CoverageMatrixV2Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillFrame | null>(null); // when set → showing district/pincode view
  const [assign, setAssign] = useState<TerritoryTarget | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      setMatrix(await leadRouterV2Api.dealerTerritoryMatrix('state'));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load dealer-territory matrix');
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const totals = useMemo(() => {
    const t = { green: 0, yellow: 0, red: 0 };
    for (const c of matrix?.cells ?? []) t[c.cell]++;
    return t;
  }, [matrix]);

  return (
    <div>
      {/* ── Prominent distinction note (spec §5) ── */}
      <div data-testid="dealer-territory-distinction" style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--violet-50, #ece7fb)',
        border: '1px solid #d9cffb', borderRadius: 10, padding: '10px 13px', marginBottom: 14,
        color: 'var(--violet, #6b4fd8)', fontSize: 12.5,
      }}>
        <MapPin size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <b>Dealer Territory — which territory a dealer is ASSIGNED to serve.</b> This is <u>not</u> where dealers
          are located. For each dealer's own base location see <b>Dealer Management → Locations</b>.
        </div>
      </div>

      <SectionHead
        title="Dealer Territory coverage"
        sub="Product category × state — is this territory SERVED by a dealer? Green = every pincode has a dealer assigned, amber = some do, red hatch = none. Drill a cell to district → pincode."
      />

      {loading && <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>Loading dealer territories…</div>}
      {error && (
        <div style={{ padding: '8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>
            <AlertCircle size={15} /> {error}
          </div>
          <button type="button" onClick={load} style={btn}><RefreshCcw size={13} /> Retry</button>
        </div>
      )}

      {!loading && !error && matrix && (
        drill
          ? <DrillView frame={drill} dealers={dealers} onBack={() => setDrill(null)} onAssign={setAssign} />
          : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
                <Stat v={totals.green} k="Fully served (green)" tone="green" />
                <Stat v={totals.yellow} k="Partially served (amber)" tone="yellow" />
                <Stat v={totals.red} k="No dealer (red)" tone="red" />
              </div>
              <MatrixTable
                matrix={matrix}
                onCell={(productGroupKey, state) => setDrill({ productGroupKey, state })}
              />
              <Legend />
            </>
          )
      )}

      {assign && (
        <AssignDealerPanel target={assign} dealers={dealers} onClose={() => { setAssign(null); }} onAssigned={() => { setAssign(null); void load(); }} />
      )}
    </div>
  );
}

// ─── State-level matrix ───────────────────────────────────────────────────────
function MatrixTable({ matrix, onCell }: {
  matrix: CoverageMatrixV2Response; onCell: (productGroupKey: string, state: string) => void;
}) {
  const byKey = useMemo(() => {
    const m = new Map<string, MatrixTone>();
    for (const c of matrix.cells) m.set(`${c.productGroupKey}|${c.state}`, c.cell);
    return m;
  }, [matrix]);

  return (
    <div style={{ overflow: 'auto', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--ivory-0)' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...th, position: 'sticky', left: 0, background: 'var(--ivory-0)', textAlign: 'left' }}>Product ╲ State</th>
            {matrix.states.map((s) => <th key={s} style={th}>{s}</th>)}
          </tr>
        </thead>
        <tbody>
          {matrix.groups.map((g) => (
            <tr key={g}>
              <td style={{ ...td, fontWeight: 700, position: 'sticky', left: 0, background: 'var(--ivory-0)', textAlign: 'left', whiteSpace: 'nowrap' }}>{g}</td>
              {matrix.states.map((s) => {
                const tone = byKey.get(`${g}|${s}`);
                if (!tone) return <td key={s} style={td} />;
                return (
                  <td key={s} data-testid={`dt-cell-${g}-${s}`} title={`${g} · ${s} — ${toneLabel(tone)} (click to drill)`}
                    onClick={() => onCell(g, s)}
                    style={{ ...td, background: TONE_BG[tone], color: TONE_FG[tone], cursor: 'pointer', textAlign: 'center' }}>
                    {TONE_GLYPH[tone]}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── District / pincode drill ─────────────────────────────────────────────────
function DrillView({ frame, dealers, onBack, onAssign }: {
  frame: DrillFrame; dealers: DealerLocus[];
  onBack: () => void; onAssign: (t: TerritoryTarget) => void;
}) {
  const [cells, setCells] = useState<Array<{ unit: string; cell: MatrixTone }> | null>(null);
  const [level, setLevel] = useState<'district' | 'pincode'>('district');
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Local breadcrumb: when a district is opened we keep it here (state stays in `frame` via parent reset otherwise).
  const [district, setDistrict] = useState<string | undefined>(frame.district);

  const fetchDrill = async (dist?: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await leadRouterV2Api.coverageDrill({ layer: 'DEALER', productGroupKey: frame.productGroupKey, state: frame.state, district: dist });
      setCells(r.cells.map((c) => ({ unit: c.unit, cell: c.cell })));
      setLevel(r.level);
      setDistrict(dist);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to drill');
    } finally { setBusy(false); }
  };
  useEffect(() => { void fetchDrill(frame.district); /* eslint-disable-next-line */ }, []);

  const targetFor = (unit: string): TerritoryTarget => (
    level === 'pincode'
      ? { matchType: 'pincode', matchValue: unit, productGroupKey: frame.productGroupKey, state: frame.state, district }
      : { matchType: 'district', matchValue: unit, productGroupKey: frame.productGroupKey, state: frame.state, district: unit }
  );

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
        <button type="button" onClick={onBack} style={{ ...btn, padding: '4px 9px', fontSize: 12 }}><ArrowLeft size={12} /> States</button>
        <ChevronRight size={12} style={{ opacity: 0.5 }} />
        <b style={{ color: 'var(--ink-1)' }}>{frame.productGroupKey}</b>
        <ChevronRight size={12} style={{ opacity: 0.5 }} />
        <span style={{ color: district && level === 'pincode' ? 'var(--ink-3)' : 'var(--ink-1)', cursor: district && level === 'pincode' ? 'pointer' : 'default', textDecoration: district && level === 'pincode' ? 'underline' : 'none' }}
          onClick={() => { if (district && level === 'pincode') void fetchDrill(undefined); }}>{frame.state}</span>
        {level === 'pincode' && district && (<><ChevronRight size={12} style={{ opacity: 0.5 }} /><span style={{ color: 'var(--ink-1)' }}>{district}</span></>)}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-0)', marginBottom: 8 }}>
        {level === 'district' ? 'Districts' : 'Pincodes'} — dealer service coverage
      </div>

      {busy && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>}
      {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}><AlertCircle size={14} /> {err}</div>}

      {!busy && !err && cells && (
        cells.length === 0
          ? <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>No {level === 'district' ? 'districts' : 'pincodes'} here.</div>
          : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 8 }}>
              {cells.map(({ unit, cell }) => (
                <div key={unit} data-testid={`dt-unit-${unit}`} style={{
                  border: '1px solid var(--line)', borderRadius: 10, background: TONE_BG[cell], padding: '9px 11px',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ color: TONE_FG[cell], fontSize: 14 }}>{TONE_GLYPH[cell]}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{unit}</div>
                    <div style={{ fontSize: 11, color: TONE_FG[cell] }}>{toneLabel(cell)}</div>
                  </div>
                  {level === 'district' && cell !== 'green' && (
                    <button type="button" onClick={() => void fetchDrill(unit)} style={{ ...btn, padding: '4px 8px', fontSize: 11 }} title="Drill to pincodes">
                      Pincodes
                    </button>
                  )}
                  {cell !== 'green' && (
                    <button type="button" onClick={() => onAssign(targetFor(unit))} style={{ ...btn, padding: '4px 8px', fontSize: 11 }}>
                      <Store size={11} /> Assign
                    </button>
                  )}
                </div>
              ))}
            </div>
          )
      )}
      <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 12 }}>
        Assigning suggests <b>nearby dealers first</b> ({dealers.length} known). A dealer can only be assigned where a rep exec already owns the territory.
      </p>
    </div>
  );
}

// ─── Assign panel: proximity-ranked picker + dealer-requires-exec guard ───────
function AssignDealerPanel({ target, dealers, onClose, onAssigned }: {
  target: TerritoryTarget; dealers: DealerLocus[]; onClose: () => void; onAssigned: () => void;
}) {
  const [detail, setDetail] = useState<TerritoryDetailV2 | null | undefined>(undefined); // undefined = loading
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [assignErr, setAssignErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setDetail(undefined); setDetailErr(null);
    leadRouterV2Api.territoryDetail({
      productGroupKey: target.productGroupKey ?? '', matchType: target.matchType as MatchTypeV2, matchValue: target.matchValue,
    }).then((d) => { if (alive) setDetail(d); })
      .catch((e) => { if (alive) { setDetail(null); setDetailErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load territory'); } });
    return () => { alive = false; };
    // productGroupKey is carried on TerritoryTarget via the drill frame (see note below)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.matchType, target.matchValue]);

  const ranked = useMemo(() => rankDealersByProximity(target, dealers), [target, dealers]);
  const blockReason = dealerAssignBlockReason(detail); // null unless hasExec === false

  async function assignDealer(d: DealerLocus) {
    if (blockReason) return; // guard: never proceed to an exec-less territory
    setBusyId(d.id); setAssignErr(null);
    try {
      await leadRouterV2Api.bulkSaveAssignments({
        assignments: [{
          layer: 'DEALER', dealerId: d.id, productGroupKey: target.productGroupKey ?? null,
          matchType: target.matchType as MatchTypeV2, matchValue: target.matchValue,
        }],
      });
      onAssigned();
    } catch (e) {
      // 422 NO_EXEC_OWNER (server backstop) → the guard copy; else generic.
      setAssignErr(dealerAssignErrorMessage(e));
    } finally { setBusyId(null); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,15,10,0.4)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(520px, 96vw)', maxHeight: '82vh', display: 'flex', flexDirection: 'column', background: 'var(--ivory-0)', border: '1px solid var(--line)', borderRadius: 14, boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '15px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-0)' }}>Assign a dealer to serve</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            {target.matchType} <b>{target.matchValue}</b>{target.state ? ` · ${target.state}` : ''} — nearest dealers first.
          </div>
        </div>

        {/* Guard banner (spec §6): no exec ⇒ block with a clear reason. */}
        {blockReason && (
          <div data-testid="dealer-guard-block" style={{
            margin: '12px 18px 0', display: 'flex', alignItems: 'flex-start', gap: 9,
            background: 'var(--warn-bg, #f7eccf)', border: '1px solid #EBD9B0', borderRadius: 10, padding: '9px 12px',
            color: 'var(--warn, #a5741a)', fontSize: 12.5,
          }}>
            <ShieldAlert size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>{blockReason}</div>
          </div>
        )}

        {detail === undefined && <div style={{ padding: 18, color: 'var(--ink-3)', fontSize: 13 }}>Checking territory ownership…</div>}
        {detailErr && <div style={{ padding: '10px 18px', color: 'var(--danger)', fontSize: 12.5 }}><AlertCircle size={14} /> {detailErr}</div>}
        {assignErr && <div style={{ padding: '8px 18px 0', color: 'var(--danger, #b23c30)', fontSize: 12.5 }}>{assignErr}</div>}

        {detail && (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '10px 12px' }}>
            {ranked.length === 0 && <div style={{ padding: 12, color: 'var(--ink-3)', fontSize: 13 }}>No dealers available. Create one in Dealer Management.</div>}
            {ranked.map((d) => {
              const label = proximityLabel(proximityScore(target, d));
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, opacity: busyId === d.id ? 0.55 : 1 }}>
                  <Store size={15} style={{ color: 'var(--ink-3)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)' }}>{d.name}</div>
                    {label && <div style={{ fontSize: 11, color: 'var(--forest, #2f7d3b)' }}>{label}</div>}
                  </div>
                  <button type="button" data-testid={`assign-dealer-${d.id}`} disabled={!!blockReason || busyId === d.id} onClick={() => void assignDealer(d)}
                    title={blockReason ?? 'Assign this dealer'}
                    style={{ ...btn, padding: '5px 11px', fontSize: 12, opacity: blockReason ? 0.5 : 1, cursor: blockReason ? 'not-allowed' : 'pointer' }}>
                    Assign
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ ...btn, background: 'var(--forest)', color: '#fff', border: 'none' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ─── local primitives (LeadRoutingPage helpers aren't exported) ───────────────
function SectionHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-0)', margin: 0 }}>{title}</h2>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 3, maxWidth: 720 }}>{sub}</div>
    </div>
  );
}
function Stat({ v, k, tone }: { v: React.ReactNode; k: string; tone: MatrixTone }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, background: 'var(--ivory-0)', padding: '10px 12px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: TONE_FG[tone] }}>{v}</div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{k}</div>
    </div>
  );
}
function Legend() {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 14, fontSize: 11.5, color: 'var(--ink-2)' }}>
      <LegendSw sw={TONE_BG.green} label="Fully served — every pincode has a dealer" />
      <LegendSw sw={TONE_BG.yellow} label="Partially served — some pincodes have a dealer, some don't" />
      <LegendSw sw={TONE_BG.red} label="No dealer assigned" />
    </div>
  );
}
function LegendSw({ sw, label }: { sw: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 14, height: 14, borderRadius: 4, background: sw, border: '1px solid var(--line)' }} /> {label}
    </span>
  );
}
function toneLabel(t: MatrixTone): string {
  return t === 'green' ? 'Fully served' : t === 'yellow' ? 'Partially served' : 'No dealer';
}

const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-1)' };
const th: React.CSSProperties = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)' };

export default DealerTerritoryTab;

// Guard copy re-export so consumers can reference the same string.
export { DEALER_REQUIRES_EXEC_MESSAGE };
